import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Users, QrCode as QrIcon, X } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const empty = { first_name: "", last_name: "", grade: "", home_address: "", home_lat: 46.5083, home_lng: 6.4977 };

export default function ParentChildren() {
  const [children, setChildren] = useState([]);
  const [showQr, setShowQr] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = async () => { const { data } = await api.get("/children"); setChildren(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/children", { ...form, home_lat: parseFloat(form.home_lat), home_lng: parseFloat(form.home_lng) });
      toast.success("Enfant ajouté"); setShowForm(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-4 pb-6" data-testid="parent-children-page">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Vos enfants</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Enfants</h1>
        </div>
        <button data-testid="parent-add-child-btn" onClick={() => setShowForm(true)} className="inline-flex items-center gap-1 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      <div className="space-y-3">
        {children.length === 0 ? (
          <div className="border border-zinc-900 p-6 text-sm text-zinc-500 text-center">Aucun enfant enregistré.</div>
        ) : children.map((c) => (
          <div key={c.id} className="border border-zinc-900 p-4 bg-white" data-testid={`parent-child-card-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-zinc-900 text-[#FFC60A] flex items-center justify-center font-display font-black text-xl">
                {c.first_name[0]}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg tracking-tight">{c.first_name} {c.last_name}</div>
                <div className="text-xs text-zinc-500 font-mono flex items-center gap-2"><Users size={12} /> {c.grade || "—"} · {c.home_address || "Domicile"}</div>
              </div>
              <button data-testid={`parent-qr-btn-${c.id}`} onClick={() => setShowQr(c)} className="p-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white">
                <QrIcon size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="parent-child-form-modal">
          <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-md">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">Nouvel enfant</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              {[["first_name", "Prénom"], ["last_name", "Nom"], ["grade", "Classe"], ["home_address", "Adresse"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{l}</label>
                  <input data-testid={`parent-child-form-${k}`} required={k !== "grade" && k !== "home_address"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full border border-zinc-900 px-3 py-2 focus:ring-2 focus:ring-zinc-900 focus:outline-none" />
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-zinc-900 font-semibold">Annuler</button>
              <button data-testid="parent-save-child-btn" type="submit" className="px-4 py-2 bg-[#FFC60A] border border-zinc-900 font-bold">Ajouter</button>
            </div>
          </form>
        </div>
      )}

      {showQr && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="parent-qr-modal">
          <div className="bg-white border border-zinc-900 w-full max-w-sm">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">{showQr.first_name}</div>
              <button onClick={() => setShowQr(null)}><X size={18} /></button>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <QRCodeSVG value={showQr.qr_code} size={220} bgColor="#ffffff" fgColor="#0A0A0B" level="M" />
              <div className="font-mono text-xs tracking-[0.15em] uppercase">{showQr.qr_code}</div>
              <p className="text-xs text-zinc-500 text-center">Montre-le au chauffeur pour la montée et la descente.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
